/**
 * ActivityForm Component - Hedita Design System
 * Feature: Activities - Form creazione/modifica attività
 */

import { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Tag, Building2, User, Clock, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivity, useCreateActivity, useUpdateActivity } from "../hooks";
import { useStrutture } from "@/features/strutture";
import type { Activity } from "../types";

const TIPI = ["Pulizie", "Manutenzione", "Check-in", "Check-out", "Altro"];
const STATI = ["da_fare", "in_corso", "completata", "annullata"];

const TIPO_CONFIG: Record<string, { bg: string; color: string; border: string }> = {
  "Pulizie": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20" },
  "Manutenzione": { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20" },
  "Check-in": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20" },
  "Check-out": { bg: "bg-purple-500/10", color: "text-purple-400", border: "border-purple-500/20" },
  "Altro": { bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border" },
};

const STATO_CONFIG: Record<string, { bg: string; color: string; border: string; label: string }> = {
  "da_fare": { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20", label: "Da fare" },
  "in_corso": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20", label: "In corso" },
  "completata": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", label: "Completata" },
  "annullata": { bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border", label: "Annullata" },
};

interface ActivityFormProps {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (activity: Activity) => void;
  defaultTipo?: string;
}

export function ActivityForm({ id, open, onOpenChange, onSaved, defaultTipo = "Pulizie" }: ActivityFormProps) {
  const isNew = !id || id === "nuovo";
  
  const { data: activity, isLoading } = useActivity(isNew ? undefined : id);
  const { data: strutture = [] } = useStrutture();
  const createMut = useCreateActivity();
  const updateMut = useUpdateActivity();

  const [form, setForm] = useState({
    tipo: defaultTipo,
    titolo: "",
    descrizione: "",
    struttura_id: "",
    struttura_nome: "",
    data: new Date().toISOString().slice(0, 10),
    ora_inizio: "",
    ora_fine: "",
    stato: "da_fare" as const,
    priorita: "media" as const,
    assegnato_a: "",
    note: "",
  });
  
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (activity) {
      setForm({
        tipo: activity.tipo || "Pulizie",
        titolo: activity.titolo || "",
        descrizione: activity.descrizione || "",
        struttura_id: activity.struttura_id || "",
        struttura_nome: activity.struttura_nome || "",
        data: activity.data ? activity.data.slice(0, 10) : new Date().toISOString().slice(0, 10),
        ora_inizio: activity.ora_inizio || "",
        ora_fine: activity.ora_fine || "",
        stato: activity.stato || "da_fare",
        priorita: activity.priorita || "media",
        assegnato_a: activity.assegnato_a || "",
        note: activity.note || "",
      });
    } else if (isNew) {
      setForm(prev => ({ ...prev, tipo: defaultTipo }));
    }
  }, [activity, defaultTipo, isNew]);

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleStrutturaChange = (strutturaId: string) => {
    const s = strutture.find(s => s.id === strutturaId);
    set("struttura_id", strutturaId);
    set("struttura_nome", s?.nome || "");
  };

  const handleSave = () => {
    if (!form.titolo.trim()) return;
    setSaveStatus("saving");
    setSaveError(null);

    const onSuccess = (res: Activity) => {
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
      onSaved?.(res);
      if (isNew) onOpenChange(false);
    };
    
    const onError = (err: Error) => {
      setSaveStatus("error");
      setSaveError(err?.message ?? "Errore di salvataggio");
    };

    if (isNew) {
      createMut.mutate(form, { onSuccess, onError });
    } else {
      updateMut.mutate({ id, data: form }, { onSuccess, onError });
    }
  };

  const tipoCfg = TIPO_CONFIG[form.tipo] || TIPO_CONFIG["Altro"];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="large" className="overflow-y-auto w-full sm:w-[90%]">
        <SheetHeader className="pb-4 border-b border-border">
          <SheetTitle className="text-lg font-bold text-foreground flex items-center gap-2">
            <span className={`w-8 h-8 rounded-lg ${tipoCfg.bg} ${tipoCfg.color} flex items-center justify-center text-sm border ${tipoCfg.border}`}>
              {form.tipo.slice(0, 2)}
            </span>
            {isNew ? "Nuova attività" : "Modifica attività"}
          </SheetTitle>
        </SheetHeader>

        {!isNew && isLoading ? (
          <div className="py-8 space-y-4">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        ) : (
          <div className="py-4 space-y-5">
            {/* Tipo */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Tipo attività</label>
              <div className="flex flex-wrap gap-2">
                {TIPI.map(t => {
                  const cfg = TIPO_CONFIG[t];
                  const active = form.tipo === t;
                  return (
                    <button
                      key={t}
                      onClick={() => set("tipo", t)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        active ? `${cfg.bg} ${cfg.color} ${cfg.border}` : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Titolo */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Titolo *</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.titolo}
                onChange={e => set("titolo", e.target.value)}
                placeholder="Es. Pulizia camera 101"
              />
            </div>

            {/* Struttura */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Struttura</label>
              <select 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.struttura_id}
                onChange={e => handleStrutturaChange(e.target.value)}
              >
                <option value="">Seleziona struttura...</option>
                {strutture.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>

            {/* Data e Orari */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Data</label>
                <input 
                  type="date"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                  value={form.data}
                  onChange={e => set("data", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Inizio</label>
                <input 
                  type="time"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                  value={form.ora_inizio}
                  onChange={e => set("ora_inizio", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Fine</label>
                <input 
                  type="time"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                  value={form.ora_fine}
                  onChange={e => set("ora_fine", e.target.value)}
                />
              </div>
            </div>

            {/* Stato */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Stato</label>
              <div className="flex flex-wrap gap-2">
                {STATI.map(s => {
                  const cfg = STATO_CONFIG[s];
                  const active = form.stato === s;
                  return (
                    <button
                      key={s}
                      onClick={() => set("stato", s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        active ? `${cfg.bg} ${cfg.color} ${cfg.border}` : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Descrizione */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Descrizione</label>
              <textarea
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50 resize-none"
                rows={3}
                value={form.descrizione}
                onChange={e => set("descrizione", e.target.value)}
                placeholder="Dettagli dell'attività..."
              />
            </div>

            {/* Note */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Note</label>
              <textarea
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50 resize-none"
                rows={2}
                value={form.note}
                onChange={e => set("note", e.target.value)}
                placeholder="Note interne..."
              />
            </div>

            {/* Status */}
            {saveStatus === "saved" && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                <CheckCircle className="h-4 w-4" /> Salvato con successo
              </div>
            )}
            {saveStatus === "error" && saveError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
                <AlertCircle className="h-4 w-4" /> {saveError}
              </div>
            )}
          </div>
        )}

        <SheetFooter className="pt-4 border-t border-border flex gap-2">
          <button 
            onClick={() => onOpenChange(false)}
            className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
          >
            Annulla
          </button>
          <button 
            onClick={handleSave}
            disabled={saveStatus === "saving" || !form.titolo.trim()}
            className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {saveStatus === "saving" ? "Salvataggio..." : isNew ? "Crea" : "Salva"}
          </button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
